import React from "react";
import * as LucideIcons from "lucide-react";
import { motion } from "motion/react";
import { animated } from "@react-spring/web";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLocalizedContent } from "../context/LanguageContext";
import { useParallax } from "../hooks/useParallax";
import { useTilt } from "../hooks/useTilt";
import SectionParallax from "./SectionParallax";

gsap.registerPlugin(ScrollTrigger);

const Skills = () => {
  const { skills, skillsSection } = useLocalizedContent();
  const contentShift = useParallax(0.015);
  const sectionRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (!sectionRef.current) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      const headerTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: ".gsap-skills-header",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      headerTimeline
        .from(".gsap-skills-badge", {
          autoAlpha: 0,
          y: 16,
          duration: 0.6,
          ease: "power3.out",
        })
        .from(
          ".gsap-skills-heading",
          {
            autoAlpha: 0,
            y: 22,
            duration: 0.8,
            ease: "power3.out",
          },
          "-=0.35",
        )
        .from(
          ".gsap-skills-desc",
          {
            autoAlpha: 0,
            y: 18,
            duration: 0.6,
            ease: "power3.out",
          },
          "-=0.35",
        );

      gsap.utils.toArray(".gsap-skill-group").forEach((group) => {
        const heading = group.querySelector(".gsap-skill-category-title");
        const cards = group.querySelectorAll(".gsap-skill-card");

        gsap.from(heading, {
          autoAlpha: 0,
          x: -18,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: group,
            start: "top 82%",
            toggleActions: "play none none reverse",
          },
        });

        gsap.from(cards, {
          autoAlpha: 0,
          y: 20,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.05,
          scrollTrigger: {
            trigger: group,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="skills"
      className="section-fade relative py-24 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black"></div>
      <SectionParallax variant="purple" />

      <div
        className="relative z-10 container mx-auto px-6 lg:px-12 transition-transform duration-500"
        style={{ transform: `translate3d(0, ${contentShift}px, 0)` }}
      >
        <div>
          {/* Section Header */}
          <div className="text-center mb-16 gsap-skills-header">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase bg-blue-500/10 text-blue-300 border border-blue-500/20 mb-4 gsap-skills-badge">
              {skillsSection.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 gsap-skills-heading">
              {skillsSection.heading}
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto gsap-skills-desc">
              {skillsSection.description}
            </p>
          </div>

          {/* Skills Grid by Category */}
          <div className="max-w-6xl mx-auto space-y-12">
            {skillsSection.categories.map((category) => {
              const categorySkills = skills.filter(
                (skill) => skill.category === category.id
              );
              if (categorySkills.length === 0) return null;

              return (
                <div key={category.id} className="space-y-6 gsap-skill-group">
                  {/* Category Header */}
                  <h3 className="text-xl font-semibold text-white/80 gsap-skill-category-title">
                    {category.name}
                  </h3>

                  {/* Skills Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {categorySkills.map((skill) => (
                      <SkillCard
                        key={skill.name}
                        skill={skill}
                        accent={category.color}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const SkillCard = ({ skill, accent }) => {
  const tilt = useTilt({ max: 12, scale: 1.05 });
  const Icon = LucideIcons[skill.icon] || LucideIcons.Code;

  return (
    <div className="gsap-skill-card">
      <animated.div
        ref={tilt.ref}
        style={tilt.style}
        {...tilt.bind}
        className="will-change-transform"
      >
        <motion.div
          className="group relative p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-blue-500/50 transition-all duration-300 hover:transform hover:scale-105"
        >
          {/* Glow Effect */}
          <div
            className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
          ></div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center gap-3">
            <motion.div
              className="p-3 rounded-xl bg-white/5 border border-white/10 group-hover:border-blue-500/30 transition-colors"
              whileHover={{ y: -4 }}
            >
              <div className="text-blue-300 group-hover:text-blue-200 transition-colors">
                <Icon className="w-6 h-6" />
              </div>
            </motion.div>
            <motion.span
              className="text-sm font-medium text-white/70 group-hover:text-white transition-colors text-center"
              whileHover={{ scale: 1.04 }}
            >
              {skill.name}
            </motion.span>
          </div>
        </motion.div>
      </animated.div>
    </div>
  );
};

export default Skills;
